import { useMemo, useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import { useProjectTotals } from '../lib/team-data'
import { pluralise } from '../lib/util'
import { binned, liveOfKind, type Client, type Project } from '../lib/types'

/**
 * Managing the prospects themselves, under the log of time spent on them.
 *
 * Prospects could be created and never anything else. The picker makes one on
 * the way to starting a timer, which is the right place to create one, but it
 * left no way to rename, delete or even see the full list -- the Clients
 * screen is clients only, and the Projects screen leaves prospect projects
 * out, both deliberately. A typo made in a hurry was permanent.
 *
 * It lives here rather than on the Clients screen because of the rule the
 * whole feature rests on: prospects and clients are never shown together.
 * Here it sits under the time logged against them, which is the only other
 * place a prospect appears.
 *
 * Deleting a prospect goes through the same bin as a client, since they are
 * rows in the same table -- out of the pickers at once, its logged time still
 * where it was, and gone for good only when somebody types its name.
 */
export function ProspectAdmin() {
  const { ws, act, setModal, confirm, canSeeAll } = useWorkspace()
  const { totals } = useProjectTotals(ws.id)
  const [open, setOpen] = useState(false)
  /** Folded prospects, by id. Everything starts open. */
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set())

  const toggleFold = (id: string) =>
    setFolded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const prospects = useMemo(() => liveOfKind(ws.clients, 'prospect'), [ws.clients])
  const inBin = useMemo(() => binned(ws.clients).filter((c) => c.isProspect), [ws.clients])

  /** Each prospect's projects, so a row can say what deleting it would strand. */
  const projectsOf = useMemo(() => {
    const by = new Map<string, Project[]>()
    for (const p of ws.projects) {
      if (!p.clientId) continue
      const list = by.get(p.clientId)
      if (list) list.push(p)
      else by.set(p.clientId, [p])
    }
    return by
  }, [ws.projects])

  const secondsOf = (projectId: string) => totals.get(projectId)?.seconds ?? 0
  const hoursOf = (projects: Project[]) =>
    projects.reduce((a, p) => a + secondsOf(p.id), 0) / 3600

  const binProspect = (c: Client) => {
    const mine = projectsOf.get(c.id) ?? []
    confirm({
      title: `Delete ${c.name}?`,
      body:
        (mine.length
          ? `${pluralise(mine.length, 'project')} will stop rolling up under it. `
          : 'No projects are attached. ') +
        'It goes to Deleted prospects below, where you can put it back.',
      cta: 'Delete prospect',
      danger: true,
      onConfirm: () => void act.binClient(c.id),
    })
  }

  /*
   * A project has no bin of its own, so this is the real thing.
   *
   * Said in full rather than softened: the entries survive and keep their
   * hours, but they lose the project, and the project's tasks and prices go
   * with it. Same warning the Projects screen gives for a client's project,
   * because it is the same delete -- and the name has to be typed once there
   * is time on it, since that is the case where something is actually lost.
   */
  const removeProject = (p: Project) => {
    const hours = secondsOf(p.id) / 3600
    confirm({
      title: `Delete ${p.name}?`,
      body: hours
        ? `${hours.toFixed(1)} hours are logged against this project. The entries are kept but become "No project", so they stop counting towards amounts. This cannot be undone.`
        : 'Nothing is logged against it yet, so nothing else is affected.',
      cta: 'Delete project',
      danger: true,
      requireText: hours ? p.name : undefined,
      onConfirm: () => void act.deleteProject(p.id),
    })
  }

  /** The end of the line, so it asks for the name. Same as the Clients bin. */
  const purge = (c: Client) =>
    confirm({
      title: `Delete ${c.name} for good?`,
      body:
        'This cannot be undone and there is no way back from it. Any projects ' +
        'that belonged to this prospect keep their time but lose the prospect ' +
        'permanently. Type the name to confirm.',
      cta: 'Delete for good',
      danger: true,
      requireText: c.name,
      onConfirm: () => void act.purgeClient(c.id),
    })

  if (!prospects.length && !inBin.length) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {prospects.length > 0 && (
        <div className="card card--clip">
          <button
            type="button"
            className="spread"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            style={{
              width: '100%',
              padding: '11px 16px',
              background: 'var(--surf2)',
              border: 0,
              cursor: 'pointer',
              gap: 8,
              textAlign: 'left',
            }}
          >
            <span
              aria-hidden
              style={{
                fontSize: 10,
                transform: open ? 'none' : 'rotate(-90deg)',
                transition: 'transform .12s',
              }}
            >
              ▾
            </span>
            <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 13 }}>
              Manage prospects
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>
              {pluralise(prospects.length, 'prospect')}
            </span>
          </button>

          {open && (
            <div
              className="eyebrow"
              style={{
                padding: '9px 16px 5px',
                borderTop: '1px solid var(--line2)',
                background: 'var(--surf2)',
              }}
            >
              Prospect
            </div>
          )}

          {open &&
            prospects.map((c) => {
              const mine = projectsOf.get(c.id) ?? []
              const shut = folded.has(c.id)
              return (
                <div key={c.id} style={{ borderTop: '1px solid var(--line2)' }}>
                  {/* The prospect, as a tree node: fold, colour, name, how
                      many projects, and the two things you can do to it. */}
                  <div className="tree-line" style={{ padding: '9px 16px 9px 10px', gap: 6 }}>
                    <button
                      type="button"
                      className="tree-twisty"
                      aria-expanded={!shut}
                      aria-label={`${shut ? 'Show' : 'Hide'} ${c.name}'s projects`}
                      onClick={() => toggleFold(c.id)}
                    >
                      <span
                        aria-hidden
                        style={{
                          display: 'inline-block',
                          transform: shut ? 'rotate(-90deg)' : 'none',
                          transition: 'transform .12s',
                        }}
                      >
                        ▾
                      </span>
                    </button>
                    <span
                      className="dot"
                      style={{ background: mine[0]?.color ?? 'var(--line)', flexShrink: 0 }}
                      aria-hidden
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="trunc" style={{ fontWeight: 500 }}>
                        {c.name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--mut)' }}>
                        {hoursOf(mine).toFixed(1)}h{c.contact ? ` · ${c.contact}` : ''}
                      </div>
                    </div>
                    <span className="tree-count">{mine.length}</span>
                    <button
                      type="button"
                      className="ibtn tree-del"
                      title="Rename"
                      aria-label={`Rename ${c.name}`}
                      onClick={() => setModal({ kind: 'client', client: c })}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="ibtn ibtn--del tree-del"
                      title="Delete"
                      aria-label={`Delete ${c.name}`}
                      onClick={() => binProspect(c)}
                    >
                      ✕
                    </button>
                  </div>

                  {!shut &&
                    mine.map((p) => (
                    <div
                      key={p.id}
                      className="spread"
                      style={{
                        padding: '6px 16px 8px 34px',
                        gap: 10,
                        background: 'var(--canvas)',
                      }}
                    >
                      <div className="row" style={{ gap: 8, minWidth: 0 }}>
                        <span
                          className="dot"
                          style={{ background: p.color, flexShrink: 0 }}
                          aria-hidden
                        />
                        <span className="trunc" style={{ fontSize: 13 }}>
                          {p.name}
                        </span>
                        {p.status === 'archived' && (
                          <span className="status status--off">Archived</span>
                        )}
                      </div>
                      <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                        <span className="mono" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                          {(secondsOf(p.id) / 3600).toFixed(1)}h
                        </span>
                        <button
                          type="button"
                          className="ibtn ibtn--del"
                          title="Delete project"
                          aria-label={`Delete ${p.name}`}
                          onClick={() => removeProject(p)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
        </div>
      )}

      {/* Same bin as the Clients screen, showing only what belongs on this
          side of the line. A binned prospect never appears over there. */}
      {canSeeAll && inBin.length > 0 && (
        <div className="card card--clip">
          <div
            className="spread"
            style={{ padding: '11px 16px', background: 'var(--surf2)', gap: 8 }}
          >
            <span style={{ fontWeight: 600, fontSize: 13 }}>Deleted prospects</span>
            <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>
              {pluralise(inBin.length, 'prospect')}
            </span>
          </div>

          {inBin.map((c) => (
            <div
              key={c.id}
              className="spread"
              style={{ padding: '11px 16px', borderTop: '1px solid var(--line2)', gap: 10 }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="trunc" style={{ fontWeight: 500 }}>
                  {c.name}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                  Deleted {c.deletedAt ? new Date(c.deletedAt).toLocaleString() : ''}
                </div>
              </div>
              <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => void act.restoreClient(c.id, c.name)}
                >
                  Restore
                </button>
                <button type="button" className="btn btn--danger" onClick={() => purge(c)}>
                  Delete for good
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
